import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  TrendingUp, TrendingDown, BarChart3, Calendar, RefreshCw, PieChart, AlertCircle, ChevronDown, ChevronUp, Pencil
} from 'lucide-react';
import { usePerformanceData } from './performance/usePerformanceData';
import { formatCurrency, formatPercentage } from './performance/performanceUtils';
import PerformanceCharts from './performance/PerformanceCharts';
import BenchmarkCompareChart from './performance/BenchmarkCompareChart';
import PerformanceBestWorst from './performance/PerformanceBestWorst';
import PerformanceBreakdowns from './performance/PerformanceBreakdowns';
import ScopedPerformanceChart from './performance/ScopedPerformanceChart';
import ComparisonChart from './performance/ComparisonChart';
import ReturnMethodologyInfo from './performance/ReturnMethodologyInfo';
import FAQLink from './faqs/FAQLink';
import TabTip from './ui/TabTip';

const SNAPSHOT_FREQUENCY_HELP = (
  <div className="space-y-2" data-testid="snapshot-frequency-help-content">
    <p className="font-semibold">Update frequency</p>
    <p className="text-slate-200">Sets the minimum gap between manual portfolio-value updates. After you press <span className="font-medium">Update Portfolio Value</span>, the button is paused until this period elapses.</p>
    <p className="text-slate-300 text-xs">It controls how often you can record fresh values — not how the chart below is grouped.</p>
  </div>
);

const CHART_PERIOD_HELP = (
  <div className="space-y-2" data-testid="chart-period-help-content">
    <p className="font-semibold">Time period</p>
    <p className="text-slate-200">Sets how your historical snapshots are grouped on the time axis below — Monthly, Quarterly or Annually buckets.</p>
    <p className="text-slate-300 text-xs">It only affects how history is displayed — not how often you record new values.</p>
  </div>
);

function MissingDataCallout({ callout, onEditInvestment }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-amber-100/50 transition-colors"
        data-testid={`missing-${callout.key}-toggle`}
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-800">
            <span className="font-semibold">{callout.count} investment{callout.count !== 1 ? 's' : ''} missing {callout.label}.</span>{' '}
            {callout.message}
          </p>
        </div>
        {callout.investments.length > 0 && (
          expanded ? <ChevronUp className="w-4 h-4 text-amber-600 shrink-0" /> : <ChevronDown className="w-4 h-4 text-amber-600 shrink-0" />
        )}
      </button>
      {expanded && callout.investments.length > 0 && (
        <div className="border-t border-amber-200 px-3 py-2 space-y-1">
          {callout.investments.map((inv, i) => (
            <div key={inv.investment_id || i} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-amber-100/50">
              <div className="flex items-center gap-2">
                <p className="text-sm text-slate-700"><span data-sensitive="asset-name">{inv.name}</span></p>
                <span className="text-xs text-slate-400 capitalize">{(inv.type || '').replace(/_/g, ' ')}</span>
              </div>
              {inv.investment_id && onEditInvestment && (
                <button
                  onClick={() => onEditInvestment(inv.investment_id)}
                  className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium px-2 py-1 rounded hover:bg-emerald-50 transition-colors"
                  data-testid={`edit-missing-${inv.investment_id}`}
                >
                  <Pencil className="w-3 h-3" />Update
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const PerformanceLoadingSkeleton = () => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <div>
        <h2 className="text-2xl font-serif font-bold text-slate-900">Portfolio Performance</h2>
        <p className="body-text mt-1">Track your investment performance over time</p>
      </div>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {[...Array(4)].map((_, i) => (
        <Card key={`skeleton-${i}`} className="portfolio-card animate-pulse">
          <CardHeader className="space-y-2">
            <div className="h-4 bg-slate-200 rounded w-3/4"></div>
            <div className="h-8 bg-slate-200 rounded w-1/2"></div>
          </CardHeader>
        </Card>
      ))}
    </div>
    <Card className="portfolio-card animate-pulse">
      <CardContent className="p-8"><div className="h-64 bg-slate-200 rounded"></div></CardContent>
    </Card>
  </div>
);

const PerformanceEmptyNoInvestments = ({ setActiveTab, setShowAddModal }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-lg sm:text-2xl font-serif font-bold text-slate-900">Portfolio Performance</h2>
      <p className="body-text mt-1">Track your investment performance over time</p>
    </div>
    <Card className="border-slate-200" data-testid="performance-empty-no-investments">
      <CardContent className="py-10 px-4 sm:px-8">
        <div className="flex flex-col items-center text-center max-w-2xl mx-auto">
          <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
            <BarChart3 className="w-7 h-7 text-emerald-600" />
          </div>
          <h3 className="text-lg sm:text-xl font-serif font-semibold text-slate-900 mb-2">Nothing to track yet</h3>
          <p className="text-sm text-slate-600 mb-5 leading-relaxed">
            Once you add some investments, this tab will track how your portfolio grows over time and surface useful insights you won&apos;t find on the Overview tab.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 w-full text-left">
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <div className="text-xs font-semibold text-slate-700 mb-1">What gets tracked</div>
              <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
                <li><span className="font-medium">Total Return</span> — money made or lost since you started</li>
                <li><span className="font-medium">% Return</span> — how that compares to what you put in</li>
                <li><span className="font-medium">Best & Worst Performers</span> — top and bottom investments</li>
                <li><span className="font-medium">Allocation Drift</span> — how your mix changes over time</li>
              </ul>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <div className="text-xs font-semibold text-slate-700 mb-1">How it works</div>
              <p className="text-xs text-slate-600 leading-relaxed">
                After you add investments, press the <span className="font-medium">Update Portfolio Value</span> button (top right of this tab) to record today&apos;s baseline. We&apos;ll then plot every update you make over time on a chart.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={() => setShowAddModal && setShowAddModal(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="performance-empty-add-btn">
              Add Your First Investment
            </Button>
            <Button variant="outline" onClick={() => setActiveTab && setActiveTab('sample')} data-testid="performance-empty-sample-btn">
              View Sample Portfolio
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

const PerformanceEmptyNoSnapshots = ({ isUpdating, updateSnapshots }) => (
  <div className="space-y-6">
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
      <div>
        <h2 className="text-lg sm:text-2xl font-serif font-bold text-slate-900">Portfolio Performance</h2>
        <p className="body-text mt-1">Track your investment performance over time</p>
      </div>
      <Button data-testid="update-snapshots-btn" onClick={updateSnapshots} disabled={isUpdating} className="bg-emerald-600 hover:bg-emerald-700 text-white">
        <RefreshCw className={`w-4 h-4 mr-2 ${isUpdating ? 'animate-spin' : ''}`} />
        {isUpdating ? 'Capturing baseline…' : 'Capture First Snapshot'}
      </Button>
    </div>
    <Card className="border-slate-200" data-testid="performance-empty-no-snapshots">
      <CardContent className="py-10 px-4 sm:px-8">
        <div className="flex flex-col items-center text-center max-w-2xl mx-auto">
          <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-4">
            <Calendar className="w-7 h-7 text-blue-600" />
          </div>
          <h3 className="text-lg sm:text-xl font-serif font-semibold text-slate-900 mb-2">Ready to start tracking</h3>
          <p className="text-sm text-slate-600 mb-4 leading-relaxed">
            Your investments are loaded, but we don&apos;t have a baseline yet. Press
            <span className="font-medium"> Capture First Snapshot</span> to record today&apos;s portfolio value — that becomes the
            starting point for every chart and return calculation on this tab.
          </p>
          <p className="text-xs text-slate-500 max-w-md">
            Tip: update the snapshot regularly (we suggest <span className="font-medium">monthly</span>) so the charts have
            enough points to be meaningful. You can change the frequency from the picker that appears once you&apos;ve captured your first one.
          </p>
        </div>
      </CardContent>
    </Card>
  </div>
);

const PerformanceTab = ({ user, onEditInvestment, setActiveTab, setShowAddModal, portfolioSummary }) => {
  const {
    performanceData, metrics, selectedPeriod, setSelectedPeriod,
    isLoading, isUpdating, missingFields, completenessBreakdown, periods, updateSnapshots,
    snapshotFrequency, frequencyOptions, updateSnapshotFrequency,
  } = usePerformanceData();

  if (isLoading) return <PerformanceLoadingSkeleton />;

  const hasInvestments = (portfolioSummary?.total_investments || 0) > 0;
  if (!hasInvestments) return <PerformanceEmptyNoInvestments setActiveTab={setActiveTab} setShowAddModal={setShowAddModal} />;

  const hasPerformanceHistory = (performanceData?.length || 0) > 0 || !!metrics;
  if (!hasPerformanceHistory) return <PerformanceEmptyNoSnapshots isUpdating={isUpdating} updateSnapshots={updateSnapshots} />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-lg sm:text-2xl font-serif font-bold text-slate-900">Portfolio Performance</h2>
          <p className="body-text mt-1">Track your investment performance over time</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end gap-2 w-full sm:w-auto">
          <div className="flex flex-col gap-1 w-full sm:w-auto">
            <label htmlFor="snapshot-frequency-select" className="text-xs font-medium text-slate-600 flex items-center" data-testid="snapshot-frequency-label">
              <span>How often to update your portfolio</span>
              <TabTip showIcon content={SNAPSHOT_FREQUENCY_HELP} />
            </label>
            <select
              id="snapshot-frequency-select"
              data-testid="snapshot-frequency-select"
              value={snapshotFrequency}
              onChange={(e) => updateSnapshotFrequency(e.target.value)}
              className="text-xs border border-slate-300 rounded-md px-2 py-1.5 bg-white text-slate-600 w-full sm:w-auto"
            >
              {frequencyOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <Button data-testid="update-snapshots-btn" onClick={updateSnapshots} disabled={isUpdating} variant="outline" size="sm" className="portfolio-button-secondary w-full sm:w-auto"
            title="Takes a snapshot of your current portfolio values to track performance trends over time. Does not change your investments.">
            <RefreshCw className={`w-4 h-4 mr-2 ${isUpdating ? 'animate-spin' : ''}`} />
            {isUpdating ? 'Updating...' : 'Update Portfolio Value'}
          </Button>
        </div>
      </div>

      {/* Data Completeness Callouts */}
      {missingFields && (() => {
        const fieldKeys = [
          { key: 'Purchase Date', label: 'Purchase Date', message: 'Time-weighted returns and annualized performance require purchase dates.' },
          { key: 'Interest Rate (%)', label: 'Interest Rate', message: 'Yield and income analysis require interest rates.' },
          { key: 'Maturity Date', label: 'Maturity Date', message: 'Bond and fixed-income analytics require maturity dates.' },
          { key: 'Share Price', label: 'Share Price', message: 'Per-unit cost basis and return calculations require share prices.' },
        ];
        const callouts = fieldKeys.filter(f => missingFields[f.key]).map(f => ({
          ...f, count: missingFields[f.key],
          investments: (completenessBreakdown || []).find(b => b.field === f.key)?.investments || [],
        }));
        if (callouts.length === 0) return null;
        return (
          <div className="space-y-2" data-testid="performance-completeness-callouts">
            {callouts.map(c => (
              <MissingDataCallout key={c.key} callout={c} onEditInvestment={onEditInvestment} />
            ))}
          </div>
        );
      })()}

      {performanceData.length === 0 ? (
        <Card className="portfolio-card">
          <CardContent className="p-12 text-center">
            <div className="space-y-4">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto">
                <BarChart3 className="w-8 h-8 text-slate-400" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-serif font-bold text-slate-900">No performance data yet</h3>
                <p className="body-text max-w-md mx-auto">
                  Performance tracking starts once you add investments. Historical data will be generated to show trends.
                </p>
                <Button data-testid="generate-performance-data-btn" onClick={updateSnapshots} disabled={isUpdating} className="portfolio-button-primary mt-4">
                  {isUpdating ? 'Generating...' : 'Generate Performance Data'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Metrics Cards */}
          {metrics && (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              <Card className="portfolio-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <div className="flex items-center gap-1.5">
                    <CardTitle className="text-sm font-medium text-slate-600">Total Return</CardTitle>
                    <ReturnMethodologyInfo />
                  </div>
                  {metrics.total_return >= 0 ? <TrendingUp className="h-4 w-4 text-emerald-600" /> : <TrendingDown className="h-4 w-4 text-red-600" />}
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-mono font-bold ${metrics.total_return >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-sensitive="monetary">{formatCurrency(metrics.total_return)}</div>
                  <p className="text-xs text-slate-500 mt-1"><span data-sensitive="rate">{formatPercentage(metrics.total_return_percentage)}</span> overall</p>
                </CardContent>
              </Card>
              <Card className="portfolio-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <div className="flex items-center gap-1.5">
                    <CardTitle className="text-sm font-medium text-slate-600">Annualized Return</CardTitle>
                    <ReturnMethodologyInfo />
                  </div>
                  <BarChart3 className="h-4 w-4 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-mono font-bold ${metrics.annualized_return >= 0 ? 'text-slate-900' : 'text-red-600'}`} data-sensitive="rate">{formatPercentage(metrics.annualized_return)}</div>
                  <p className="text-xs text-slate-500 mt-1">Over {metrics.days_tracked} days</p>
                </CardContent>
              </Card>
              <Card className="portfolio-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Avg Monthly Return</CardTitle>
                  <Calendar className="h-4 w-4 text-purple-600" />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-mono font-bold ${metrics.average_monthly_return >= 0 ? 'text-slate-900' : 'text-red-600'}`} data-sensitive="rate">{formatPercentage(metrics.average_monthly_return)}</div>
                  <p className="text-xs text-slate-500 mt-1">Average per month</p>
                </CardContent>
              </Card>
              <Card className="portfolio-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Total Investments</CardTitle>
                  <PieChart className="h-4 w-4 text-orange-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-mono font-bold text-slate-900">{metrics.total_investments || 0}</div>
                  <p className="text-xs text-slate-500 mt-1">Active investments</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Period Selector */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-600 flex items-center" data-testid="chart-period-label">
              <span>How to view your value history</span>
              <TabTip showIcon content={CHART_PERIOD_HELP} />
            </label>
            <div className="flex flex-wrap gap-2">
              {periods.map((period) => (
                <Button key={period.value} data-testid={`period-${period.value}-btn`} onClick={() => setSelectedPeriod(period.value)} variant={selectedPeriod === period.value ? "default" : "outline-solid"} size="sm" className={selectedPeriod === period.value ? "portfolio-button-primary" : "portfolio-button-secondary"}>
                  {period.label}
                </Button>
              ))}
            </div>
          </div>

          <PerformanceCharts performanceData={performanceData} selectedPeriod={selectedPeriod} />
          <ScopedPerformanceChart />
          <ComparisonChart />
          <BenchmarkCompareChart />
          <PerformanceBestWorst metrics={metrics} />
          <PerformanceBreakdowns metrics={metrics} />
          <div className="pt-1">
            <FAQLink categoryKey="performance" />
          </div>
        </>
      )}
    </div>
  );
};

export default PerformanceTab;
